import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  TextField,
  CircularProgress,
  Grid,
  Divider,
} from '@material-ui/core';
import {
  createPreset,
  listPresets,
  updatePreset,
  deletePreset,
} from '~/services/hinovaFilterPresetsDb';
import { HinovaFilterPreset, HinovaFilterPresetFilters } from '~/types';
import Button from '~/components/Button';

function getUserId(state: { auth: { user?: { sub?: string; email?: string } } }): string {
  const user = state.auth?.user;
  return user?.sub ?? user?.email ?? 'anonymous';
}

interface FilterPresetManagerProps {
  currentFilters: HinovaFilterPresetFilters;
  onLoadPreset: (filters: HinovaFilterPresetFilters) => void;
  onSnack: (message: string, severity: 'success' | 'warning' | 'error' | 'info') => void;
  hasAtLeastOneFilter: boolean;
  buttonStyle?: React.CSSProperties;
}

export default function FilterPresetManager({
  currentFilters,
  onLoadPreset,
  onSnack,
  hasAtLeastOneFilter,
  buttonStyle = {},
}: FilterPresetManagerProps) {
  const userId = useSelector(getUserId);
  const [presets, setPresets] = useState<HinovaFilterPreset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [openListDialog, setOpenListDialog] = useState(false);
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPreset, setEditingPreset] = useState<HinovaFilterPreset | null>(null);
  const [editPresetName, setEditPresetName] = useState('');
  const [updatingPreset, setUpdatingPreset] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<HinovaFilterPreset | null>(null);
  const [deletingPreset, setDeletingPreset] = useState(false);

  const fetchPresets = useCallback(async () => {
    try {
      setLoadingPresets(true);
      const data = await listPresets(userId);
      setPresets(data ?? []);
    } catch (err) {
      onSnack('Não foi possível carregar filtros salvos.', 'warning');
    } finally {
      setLoadingPresets(false);
    }
  }, [userId, onSnack]);

  const handleOpenListDialog = useCallback(() => {
    setOpenListDialog(true);
    fetchPresets();
  }, [fetchPresets]);

  const handleCloseListDialog = useCallback(() => {
    setOpenListDialog(false);
  }, []);

  const handleOpenSaveDialog = useCallback(() => {
    if (!hasAtLeastOneFilter) {
      onSnack('Selecione pelo menos um filtro antes de salvar.', 'warning');
      return;
    }
    setSavePresetName('');
    setOpenSaveDialog(true);
  }, [hasAtLeastOneFilter, onSnack]);

  const handleCloseSaveDialog = useCallback(() => {
    setOpenSaveDialog(false);
    setSavePresetName('');
  }, []);

  const handleSavePreset = useCallback(async () => {
    const name = savePresetName.trim();
    if (!name) {
      onSnack('Informe um nome para o filtro.', 'warning');
      return;
    }
    try {
      setSavingPreset(true);
      await createPreset(userId, name, currentFilters);
      onSnack('Filtro salvo com sucesso.', 'success');
      handleCloseSaveDialog();
      fetchPresets();
    } catch (err) {
      onSnack('Não foi possível salvar o filtro.', 'error');
    } finally {
      setSavingPreset(false);
    }
  }, [userId, savePresetName, currentFilters, onSnack, handleCloseSaveDialog, fetchPresets]);

  const handleLoadPreset = useCallback(
    (preset: HinovaFilterPreset) => {
      onLoadPreset(preset.filters);
      handleCloseListDialog();
      onSnack(`Filtro "${preset.name}" carregado.`, 'info');
    },
    [onLoadPreset, onSnack, handleCloseListDialog],
  );

  const handleOpenEditDialog = useCallback((preset: HinovaFilterPreset) => {
    setEditingPreset(preset);
    setEditPresetName(preset.name);
    setOpenEditDialog(true);
    handleCloseListDialog();
  }, [handleCloseListDialog]);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setEditingPreset(null);
    setEditPresetName('');
  }, []);

  const handleUpdatePreset = useCallback(async () => {
    if (!editingPreset) return;
    const name = editPresetName.trim();
    if (!name) {
      onSnack('Informe um nome para o filtro.', 'warning');
      return;
    }
    try {
      setUpdatingPreset(true);
      await updatePreset(editingPreset.id, userId, { name, filters: currentFilters });
      onSnack('Filtro atualizado com sucesso.', 'success');
      handleCloseEditDialog();
      fetchPresets();
    } catch (err) {
      onSnack('Não foi possível atualizar o filtro.', 'error');
    } finally {
      setUpdatingPreset(false);
    }
  }, [userId, editingPreset, editPresetName, currentFilters, onSnack, handleCloseEditDialog, fetchPresets]);

  const handleLoadPresetForEdit = useCallback(() => {
    if (editingPreset) {
      onLoadPreset(editingPreset.filters);
      handleCloseEditDialog();
      onSnack(
        'Filtros carregados. Altere os valores e clique em "Atualizar filtro" na lista de filtros salvos para salvar.',
        'info',
      );
    }
  }, [editingPreset, onLoadPreset, handleCloseEditDialog, onSnack]);

  const handleOpenDeleteConfirm = useCallback((preset: HinovaFilterPreset) => {
    setPresetToDelete(preset);
    setOpenDeleteConfirm(true);
    handleCloseListDialog();
  }, [handleCloseListDialog]);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenDeleteConfirm(false);
    setPresetToDelete(null);
  }, []);

  const handleDeletePreset = useCallback(async () => {
    if (!presetToDelete) return;
    try {
      setDeletingPreset(true);
      await deletePreset(presetToDelete.id, userId);
      onSnack('Filtro removido.', 'success');
      handleCloseDeleteConfirm();
      fetchPresets();
    } catch (err) {
      onSnack('Não foi possível remover o filtro.', 'error');
    } finally {
      setDeletingPreset(false);
    }
  }, [userId, presetToDelete, onSnack, handleCloseDeleteConfirm, fetchPresets]);

  return (
    <>
      <Button
        label="Salvar filtro atual"
        onClick={handleOpenSaveDialog}
        disabled={!hasAtLeastOneFilter}
        filled
        style={buttonStyle}
      />
      <Button
        label="Filtros salvos"
        onClick={handleOpenListDialog}
        disabled={loadingPresets}
        filled
        style={buttonStyle}
      />

      {/* Dialog: Lista de filtros salvos */}
      <Dialog open={openListDialog} onClose={handleCloseListDialog} maxWidth="md" fullWidth>
        <DialogTitle>Filtros salvos</DialogTitle>
        <DialogContent>
          {loadingPresets ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <CircularProgress />
            </div>
          ) : (
            <List style={{ padding: 0 }}>
              {presets.map((preset, index) => (
                <React.Fragment key={preset.id}>
                  <ListItem style={{ padding: '16px 0' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <ListItemText
                          primary={preset.name}
                          primaryTypographyProps={{
                            style: { fontWeight: 500, fontSize: '16px' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            label="Carregar"
                            onClick={() => handleLoadPreset(preset)}
                            filled
                            style={{ marginTop: 0, width: 'auto', minWidth: 90 }}
                          />
                          <Button
                            label="Editar"
                            onClick={() => handleOpenEditDialog(preset)}
                            filled
                            style={{ marginTop: 0, width: 'auto', minWidth: 90 }}
                          />
                          <Button
                            label="Excluir"
                            onClick={() => handleOpenDeleteConfirm(preset)}
                            filled
                            style={{ marginTop: 0, width: 'auto', minWidth: 90 }}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {index < presets.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {presets.length === 0 && !loadingPresets && (
                <ListItem style={{ padding: '24px' }}>
                  <ListItemText
                    primary="Nenhum filtro salvo"
                    secondary="Salve um filtro para reutilizá-lo."
                    style={{ textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            label="Fechar"
            onClick={handleCloseListDialog}
            filled
            style={{ marginTop: 0, width: 'auto', minWidth: 100 }}
          />
        </DialogActions>
      </Dialog>

      {/* Dialog: Salvar novo filtro */}
      <Dialog open={openSaveDialog} onClose={handleCloseSaveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Salvar filtro</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do filtro"
            value={savePresetName}
            onChange={e => setSavePresetName(e.target.value)}
            margin="normal"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            label="Cancelar"
            onClick={handleCloseSaveDialog}
            disabled={savingPreset}
            style={{ marginTop: 0, minWidth: 120 }}
          />
          <Button
            label="Salvar"
            filled
            loading={savingPreset}
            onClick={handleSavePreset}
            style={{ marginTop: 0, minWidth: 120 }}
          />
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar preset (carregar filtros no formulário ou atualizar nome + filtros atuais) */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Editar filtro</DialogTitle>
        <DialogContent>
          {editingPreset && (
            <>
              <TextField
                fullWidth
                label="Nome do filtro"
                value={editPresetName}
                onChange={e => setEditPresetName(e.target.value)}
                margin="normal"
                variant="outlined"
              />
              <p style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
                Carregar os filtros no formulário para editá-los, ou altere o nome acima e use os
                filtros atuais do formulário para atualizar.
              </p>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', justifyContent: 'flex-end' }}>
            <Button
              label="Cancelar"
              onClick={handleCloseEditDialog}
              disabled={updatingPreset}
              style={{ marginTop: 0, minWidth: 120 }}
            />
            <Button
              label="Carregar no formulário"
              onClick={handleLoadPresetForEdit}
              filled
              style={{ marginTop: 0, minWidth: 160 }}
            />
            <Button
              label="Atualizar filtro"
              filled
              loading={updatingPreset}
              onClick={handleUpdatePreset}
              style={{ marginTop: 0, minWidth: 140 }}
            />
          </div>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar exclusão */}
      <Dialog open={openDeleteConfirm} onClose={handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir filtro</DialogTitle>
        <DialogContent>
          {presetToDelete && (
            <p>
              Deseja realmente excluir o filtro &quot;{presetToDelete.name}&quot;?
            </p>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            label="Cancelar"
            onClick={handleCloseDeleteConfirm}
            disabled={deletingPreset}
            style={{ marginTop: 0, minWidth: 120 }}
          />
          <Button
            label="Excluir"
            filled
            loading={deletingPreset}
            onClick={handleDeletePreset}
            style={{ marginTop: 0, minWidth: 120 }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
